/**
 * RFC 4180 CSV builder for the reimbursement report (pure, unit tested).
 * Semicolon is the delimiter: the report is opened in Excel with a Russian
 * locale, where comma-as-delimiter silently produces one wide column.
 */

import type { ReimbursementReport, ReimbursementRow } from '../api/types';
import { formatDate } from './format';
import { groupReimbursementRows } from './report-groups';

const DELIMITER = ';';

function escapeCell(value: string): string {
  if (value.includes('"') || value.includes(DELIMITER) || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function numberCell(value: number | null): string {
  if (value === null) return '';
  // Plain dot decimals + no thousands separator keeps the file parseable in
  // every locale; Excel's RU import maps `.` to its own decimal separator.
  return String(value);
}

export const REIMBURSEMENT_COLUMNS = [
  'Service',
  'Group',
  'Date',
  'Amount',
  'Currency',
  'RUB',
  'FX rate',
  'Rate source',
  'Flow',
  'Type',
  'Assigned users',
  'Bills',
  'Note',
] as const;

function billCell(row: ReimbursementRow): string {
  if (row.bills.length === 0) return '';
  return row.bills.map((bill) => bill.originalName ?? bill.id).join(' | ');
}

export function reimbursementRowCells(row: ReimbursementRow): string[] {
  return [
    row.subscriptionName,
    row.groupName ?? '',
    formatDate(row.occurredAt),
    numberCell(row.paidAmount),
    row.paidCurrency,
    numberCell(row.rubAmount),
    numberCell(row.fxRate),
    row.rateSource ?? '',
    row.flow,
    row.isCommission ? 'commission' : 'subscription',
    row.assignedUsers.join(', '),
    billCell(row),
    row.note ?? '',
  ];
}

export function buildReimbursementCsv(report: ReimbursementReport): string {
  const lines: string[] = [
    [`Reimbursement report ${formatDate(report.from)} — ${formatDate(report.to)}`].map(escapeCell).join(DELIMITER),
    [...REIMBURSEMENT_COLUMNS].map(escapeCell).join(DELIMITER),
  ];

  for (const row of report.rows) {
    lines.push(reimbursementRowCells(row).map(escapeCell).join(DELIMITER));
  }

  // The table groups rows under vendor-group headers with subtotals; the file
  // carries the same rollup so the printout, the screen and the export agree.
  // Subtotal figures sit in the same cells the TOTALS block below uses.
  for (const group of groupReimbursementRows(report.rows)) {
    const label = `${group.label} — subtotal (${group.eventsCount} payment${group.eventsCount === 1 ? '' : 's'})`;
    lines.push(['', label, '', numberCell(group.rubTotal), 'RUB'].map(escapeCell).join(DELIMITER));
    // The ₽ line above already states the rubles; saying RUB again would be
    // the same number twice for a group paid entirely in rubles.
    for (const [currency, amount] of Object.entries(group.paidByCurrency)) {
      if (currency === 'RUB') continue;
      lines.push(['', label, '', numberCell(amount), currency].map(escapeCell).join(DELIMITER));
    }
  }

  lines.push('');
  lines.push(['TOTALS'].map(escapeCell).join(DELIMITER));
  for (const [currency, amount] of Object.entries(report.totals.paidByCurrency)) {
    lines.push(['', '', '', numberCell(amount), currency].map(escapeCell).join(DELIMITER));
  }
  lines.push(['', '', '', numberCell(report.totals.rubTotal), 'RUB'].map(escapeCell).join(DELIMITER));
  lines.push(
    ['', '', 'Subscriptions only', numberCell(report.totals.subscriptionsOnly.rubTotal), 'RUB']
      .map(escapeCell)
      .join(DELIMITER),
  );
  lines.push(
    ['', '', 'Commissions', numberCell(report.totals.commissions.rubTotal), 'RUB']
      .map(escapeCell)
      .join(DELIMITER),
  );

  // UTF-8 BOM: Excel otherwise reads the Cyrillic receipt names as mojibake.
  return `﻿${lines.join('\r\n')}`;
}

/** Triggers a browser download of `content` under `filename`. */
export function downloadBlob(content: BlobPart, filename: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
